import { eventMessages } from 'src/features/eventLog/utils/messages';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import {
    BANKNOTE_VALIDATOR_MANUFACTURER,
    CAMERA_TYPE,
    CCTALK_INTERFACE_MODAL,
    COIN_HOPPER_MANUFACTURER,
    COMMUNICATOR_TYPE,
    GATEWAY_DISPLAY_MODEL,
    IDLE_STATE,
    LISTEN_STATE,
    MEMBERSHIP_CATEGORY,
    ORIENTATION,
    SYSTEM_COMPONENT,
    SYSTEM_COMPONENT_STATUS,
    SYSTEM_COMPONENT_TYPE,
} from 'src/features/systems/utils/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { BadgeOption } from 'src/registry/types/badges';

export const membershipCategoryTranslationMap: Record<MEMBERSHIP_CATEGORY, { id: string; defaultMessage: string }> = {
    [MEMBERSHIP_CATEGORY.VIP]: visitorMessages.vip,
    [MEMBERSHIP_CATEGORY.RESIDENT]: visitorMessages.resident,
    [MEMBERSHIP_CATEGORY.STAFF]: visitorMessages.staff,
    [MEMBERSHIP_CATEGORY.SERVICE]: visitorMessages.service,
    [MEMBERSHIP_CATEGORY.SUBSCRIPTION]: visitorMessages.subscription,
    [MEMBERSHIP_CATEGORY.RESERVATION]: visitorMessages.reservation,
};

export const eventTypeTranslationMap: Record<EVENT_TYPE, { id: string; defaultMessage: string }> = {
    [EVENT_TYPE.LPR_CAMERA_DETECTION]: eventMessages.lprCameraDetection,
    [EVENT_TYPE.ACCESS_KEY_READER_DETECTION]: eventMessages.accessKeyReaderDetection,
    [EVENT_TYPE.IDENTIFIER_CREATED]: eventMessages.identifierCreated,
    [EVENT_TYPE.VISITOR_IDENTIFIED]: eventMessages.visitorIdentifier,
    [EVENT_TYPE.GATEWAY_PASS_ALLOWED]: eventMessages.gatewayPassAllowed,
    [EVENT_TYPE.GATEWAY_PASS_NOT_ALLOWED]: eventMessages.gatewayPassNotAllowed,
    [EVENT_TYPE.ACTIVE_STAY_CREATED]: eventMessages.activeStayCreated,
    [EVENT_TYPE.ACTIVE_STAY_PERIOD_CREATED]: eventMessages.activeStayPeriodCreated,
    [EVENT_TYPE.ACTIVE_STAY_PERIOD_COMPLETED]: eventMessages.activeStayPeriodCompleted,
    [EVENT_TYPE.ACTIVE_STAY_COMPLETED]: eventMessages.activeStayCompleted,
    [EVENT_TYPE.ACTIVE_STAY_PAID]: eventMessages.activeStayPaid,
    [EVENT_TYPE.GATEWAY_OPENED]: eventMessages.gatewayOpened,
    [EVENT_TYPE.GATEWAY_CLOSED]: eventMessages.gatewayClosed,
};

export const SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP = {
    [SYSTEM_COMPONENT_TYPE.SOFTWARE]: systemsMessages.software,
    [SYSTEM_COMPONENT_TYPE.HARDWARE_DEVICE]: systemsMessages.hardware,
};

export const COMPONENT_DATA_TYPE_TRANSLATIONS_MAP = {
    [CAMERA_TYPE.SP_LPR]: systemsMessages.spLPR,
    [COMMUNICATOR_TYPE.SP_MQTT]: systemsMessages.spMQTT,
};

export const GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP = {
    [GATEWAY_DISPLAY_MODEL.SP]: systemsMessages.spDisplayModal,
    [GATEWAY_DISPLAY_MODEL.HUIDU]: systemsMessages.huiduDisplayModal,
    [GATEWAY_DISPLAY_MODEL.NED200]: systemsMessages.ned200DisplayModal,
};

export const LISTEN_STATE_TRANSLATIONS_MAP = {
    [LISTEN_STATE.OPEN]: systemsMessages.open,
    [LISTEN_STATE.CLOSED]: systemsMessages.closed,
};

export const IDLE_STATE_TRANSLATIONS_MAP = {
    [IDLE_STATE.DATE]: systemsMessages.idleStateDate,
    [IDLE_STATE.SPACES]: systemsMessages.idleStateSpaces,
    [IDLE_STATE.LPR]: systemsMessages.idleStateLprDetection,
    [IDLE_STATE.TICKET]: systemsMessages.idleStateTicketDetection,
    [IDLE_STATE.CARD]: systemsMessages.idleStateCardDetection,
};

export const ORIENTATION_TRANSLATIONS_MAP = {
    [ORIENTATION.PORTRAIT]: systemsMessages.portrait,
    [ORIENTATION.LANDSCAPE]: systemsMessages.landscape,
};

export const MANUFACTURER_TRANSLATION_MAP = {
    [BANKNOTE_VALIDATOR_MANUFACTURER.JCM]: systemsMessages.jcm,
    [BANKNOTE_VALIDATOR_MANUFACTURER.INNOVATIVE_TECHNOLOGY]: systemsMessages.innovativeTechnology,
    [COIN_HOPPER_MANUFACTURER.WH_MUNZPRUFER]: systemsMessages.whMunzprufer,
};

export const CCTALK_INTERFACE_TRANSLATION_MAP = {
    [CCTALK_INTERFACE_MODAL.CCT_900]: systemsMessages.cct900,
    [CCTALK_INTERFACE_MODAL.CCT_910]: systemsMessages.cct910,
};

export const SYSTEM_COMPONENT_TRANSLATIONS_MAP = {
    [SYSTEM_COMPONENT.LPR_CAMERA]: systemsMessages.LPRCamera,
    [SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR]: systemsMessages.keyReaderCommunicator,
    [SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR]: systemsMessages.gatewayCommunicator,
    [SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR]: systemsMessages.sensorReaderCommunicator,
    [SYSTEM_COMPONENT.BANKNOTE_VALIDATOR]: systemsMessages.banknoteValidator,
    [SYSTEM_COMPONENT.COIN_HOPPER]: systemsMessages.coinHopper,
    [SYSTEM_COMPONENT.COIN_MECHANISM]: systemsMessages.coinMechanism,
    [SYSTEM_COMPONENT.COIN_ACCEPTOR]: systemsMessages.coinAcceptor,
    [SYSTEM_COMPONENT.CCTALK_INTERFACE]: systemsMessages.cctalkInterface,
    [SYSTEM_COMPONENT.GATEWAY_DISPLAY]: systemsMessages.gatewayDisplay,
};

export const ComponentTypeBadgeOptions: Record<SYSTEM_COMPONENT_TYPE, BadgeOption> = {
    [SYSTEM_COMPONENT_TYPE.SOFTWARE]: {
        label: systemsMessages.software,
        icon: ICON_MAP.CLOUD,
        theme: 'green',
    },
    [SYSTEM_COMPONENT_TYPE.HARDWARE_DEVICE]: {
        label: systemsMessages.hardware,
        icon: ICON_MAP.HARDWARE,
        theme: 'blue',
    },
};

export const ComponentBadgeOptions: Record<SYSTEM_COMPONENT, BadgeOption> = {
    [SYSTEM_COMPONENT.LPR_CAMERA]: {
        label: systemsMessages.LPRCamera,
        icon: ICON_MAP.CCTV_CAMERA,
        theme: 'yellow',
    },
    [SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR]: {
        label: systemsMessages.gatewayCommunicator,
        icon: ICON_MAP.ROAD_BARRIER,
        theme: 'indigo',
    },
    [SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR]: {
        label: systemsMessages.sensorReaderCommunicator,
        icon: ICON_MAP.SENSOR,
        theme: 'lime',
    },
    [SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR]: {
        label: systemsMessages.keyReaderCommunicator,
        icon: ICON_MAP.SUBSCRIPTION,
        theme: 'pink',
    },
    [SYSTEM_COMPONENT.BANKNOTE_VALIDATOR]: {
        label: systemsMessages.banknoteValidator,
        icon: ICON_MAP.CASH,
        theme: 'green',
    },
    [SYSTEM_COMPONENT.COIN_HOPPER]: {
        label: systemsMessages.coinHopper,
        icon: ICON_MAP.COIN,
        theme: 'orange',
    },
    [SYSTEM_COMPONENT.COIN_MECHANISM]: {
        label: systemsMessages.coinMechanism,
        icon: ICON_MAP.COIN,
        theme: 'fuchsia',
    },
    [SYSTEM_COMPONENT.COIN_ACCEPTOR]: {
        label: systemsMessages.coinAcceptor,
        icon: ICON_MAP.COIN,
        theme: 'teal',
    },
    [SYSTEM_COMPONENT.CCTALK_INTERFACE]: {
        label: systemsMessages.cctalkInterface,
        icon: ICON_MAP.SQUARE_PARKING,
        theme: 'amber',
    },
    [SYSTEM_COMPONENT.GATEWAY_DISPLAY]: {
        label: systemsMessages.gatewayDisplay,
        icon: ICON_MAP.ROAD_BARRIER,
        theme: 'blue',
    },
};

export const ListOfBanknoteCoinComponents = [
    SYSTEM_COMPONENT.BANKNOTE_VALIDATOR,
    SYSTEM_COMPONENT.COIN_HOPPER,
    SYSTEM_COMPONENT.COIN_MECHANISM,
    SYSTEM_COMPONENT.COIN_ACCEPTOR,
    SYSTEM_COMPONENT.CCTALK_INTERFACE,
];

export const ComponentStatusBadgeOptions: Record<SYSTEM_COMPONENT_STATUS, BadgeOption> = {
    [SYSTEM_COMPONENT_STATUS.OPERATIONAL]: {
        label: systemsMessages.operational,
        theme: 'green',
    },
    [SYSTEM_COMPONENT_STATUS.ERROR]: {
        label: systemsMessages.error,
        theme: 'red',
    },
    [SYSTEM_COMPONENT_STATUS.WARNING]: {
        label: systemsMessages.warning,
        theme: 'yellow',
    },
    [SYSTEM_COMPONENT_STATUS.NO_CONNECTION]: {
        label: systemsMessages.noConnection,
        theme: 'yellow',
    },
};

export const COMPONENT_PANEL = 'component-panel';
